<script lang="ts">
import { defineComponent, h, type PropType, type VNode } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { NavBarItem } from '@blpde/nx-ui';
import type { AppId } from '@/integration';
import type { NavItem } from '@/integration/nav';
import { activateNavItem } from '@/integration/navAction';
import { appMessages } from '@/i18n/appI18n';

/**
 * Renders a NavItem (and its children) into the nx-ui <NavBarItem> tree.
 *
 * Implemented as a render function rather than a recursive SFC template: a
 * self-referential `<ShellNavItem>` template resolves asynchronously, which
 * desyncs NavBarItem's onMounted registration order — and the NavBar renders
 * its rows in that registration order. `h()` recursion mounts synchronously,
 * so registration (and thus the rendered left→right order) matches the order
 * of the items array exactly.
 */
export default defineComponent({
  name: 'ShellNavItem',
  props: {
    item: { type: Object as PropType<NavItem>, required: true },
    /** The app the item belongs to: its text is a key of that app's table (I-2284). */
    app: { type: String as PropType<AppId>, default: undefined },
  },
  setup(props) {
    const router = useRouter();
    const shell = useI18n();

    // Honour the NavItem contract: `text` is either a raw label or an i18n key.
    // Resolve it only if the key actually exists, so literal labels (e.g. erp's
    // plain-text nav labels) pass through untouched without missing-key warnings.
    const label = (text: string) => {
      if (props.app) {
        const { t, te } = appMessages(props.app);
        return te(text) ? t(text) : text;
      }
      return shell.te(text) ? shell.t(text) : text;
    };

    // A leaf with a router target renders as a real link (`href`), so middle
    // click and Ctrl-/Cmd-click open it in a new tab; a plain click still
    // routes in place through onAction below.
    const hrefFor = (item: NavItem): string | undefined => {
      if (item.action || !item.to || item.children?.length) return undefined;
      try {
        return router.resolve(item.to as never).href;
      } catch {
        return undefined;
      }
    };

    // A root entry names itself to the NavBar (`itemKey`), so a bar whose app
    // lets the user put it in order (`NavSpec.reorder`) can report the new
    // order by id; a `fixed` entry keeps its place.
    function render(item: NavItem, root = true): VNode {
      return h(
        NavBarItem,
        {
          itemKey: root && !item.fixed ? item.id : undefined,
          text: label(item.text),
          icon: item.icon,
          iconOnly: item.iconOnly,
          disabled: item.disabled,
          badge: item.badge,
          href: hrefFor(item),
          panel: item.panel,
          onAction: () => activateNavItem(item, router),
        },
        () => (item.children ?? []).map((child) => render(child, false)),
      );
    }

    return () => render(props.item);
  },
});
</script>
